// WhoWeServePage.jsx
import React from "react";
import PropTypes from "prop-types";
import "./WhoWeServePage.css";

/* ---------- tiny hook: reveal on scroll (same as Blog) ---------- */
function useReveal(options = { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }) {
  const ref = React.useRef(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    if (!ref.current || inView) return;
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView, options]);

  return [ref, inView];
}

/* ---------- helpers ---------- */
const Accent = ({ children }) => <span className="accent">{children}</span>;

const Accentify = ({ text, accent }) => {
  if (!text) return null;
  if (!accent || !text.toLowerCase().includes(accent.toLowerCase())) return text;
  const idx = text.toLowerCase().indexOf(accent.toLowerCase());
  const before = text.slice(0, idx);
  const mid = text.slice(idx, idx + accent.length);
  const after = text.slice(idx + accent.length);
  return (
    <>
      {before}
      <Accent>{mid}</Accent>
      {after}
    </>
  );
};

/* ---------- sections (1:1 with your BlogPage) ---------- */
const Hero = ({ hero }) => {
  if (!hero) return null;

  return (
    <header
      className="bp-hero"
      style={{
        backgroundImage: hero.bgImage ? `url(${hero.bgImage})` : undefined,
        backgroundPosition: hero.imagePosition || "center",
        backgroundSize: hero.bgSize || "cover",
      }}
      aria-label="Who we serve hero"
    >
      <div className="bp-hero__overlay">
        <div className="bp-hero__panel">
          {hero.kicker && <p className="bp-hero__kicker">{hero.kicker}</p>}
          {hero.title && <h1 className="bp-hero__title">{hero.title}</h1>}
          
          <a className="bp-btn" href="/contact">
            Talk to an Expert
          </a>
        </div>
      </div>
    </header>
  );
};

const Lead = ({ eyebrow, title, accent, copy }) => {
  const [ref, inView] = useReveal();
  if (!eyebrow && !title && !copy) return null;

  return (
    <section ref={ref} className={`lead-block reveal ${inView ? "in" : ""}`}>
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      {title && (
        <h2 className="mega" style={{ transitionDelay: "120ms" }}>
          <Accentify text={title} accent={accent} />
        </h2>
      )}
      {copy && (
        <p className="lead" style={{ transitionDelay: "220ms" }}>
          {copy}
        </p>
      )}
    </section>
  );
};

const Block = ({ block, flip }) => {
  // keep hooks unconditional
  const [mediaRef, mediaIn] = useReveal();
  const [contentRef, contentIn] = useReveal();

  if (!block) return null;

  const mediaDir = flip ? "reveal--right" : "reveal--left";
  const contentDir = flip ? "reveal--left" : "reveal--right";

  const hasList = Array.isArray(block.points) && block.points.length > 0;
  const hasButtons = Array.isArray(block.buttons) && block.buttons.length > 0;

  return (
    <section className={`bp-split ${flip ? "bp-split--flip" : ""}`}>
      <div
        ref={mediaRef}
        className={`bp-split__media reveal ${mediaDir} ${mediaIn ? "in" : ""}`}
        style={{ transitionDelay: "60ms" }}
      >
        {block.image && <img src={block.image} alt={block.imageAlt || ""} loading="lazy" />}
      </div>

      <div
        ref={contentRef}
        className={`bp-split__content reveal ${contentDir} ${contentIn ? "in" : ""}`}
      >
        {block.eyebrow && <div className="bp-eyebrow">{block.eyebrow}</div>}

        {block.title && (
          <h3 className="bp-split__title" style={{ transitionDelay: "120ms" }}>
            <Accentify text={block.title} accent={block.accent} />
          </h3>
        )}

        {block.copy && (
          <>
            {Array.isArray(block.copy) ? (
              block.copy.map((paragraph, idx) => (
                <p key={idx} className="bp-split__copy" style={{ transitionDelay: "220ms" }}>
                  {paragraph}
                </p>
              ))
            ) : (
              <p className="bp-split__copy" style={{ transitionDelay: "220ms" }}>
                {block.copy}
              </p>
            )}
          </>
        )}

        {hasList && (
          <ul className="bp-split__list">
            {block.points.map((p, i) => (
              <li key={i} style={{ transitionDelay: `${260 + i * 80}ms` }}>
                {p}
              </li>
            ))}
          </ul>
        )}

        {block.quote && (
          <blockquote className="bp-quote" style={{ transitionDelay: "240ms" }}>
            “{block.quote}”
            {block.cite && <cite>— {block.cite}</cite>}
          </blockquote>
        )}

        {hasButtons && (
          <div className="bp-buttons">
            {block.buttons.map((b, i) => (
              <a key={i} className="bp-primary" href={b.href}>
                {b.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ---------- page ---------- */
export default function WhoWeServePage({ data }) {
  const [currentPage, setCurrentPage] = React.useState(0);
  const blocksPerPage = 3;
  
  if (!data) return null;

  // Only apply pagination to financial-industry, government-services, events, and warehouses pages
  const usePagination = data.slug === 'financial-industry' || data.slug === 'government-services' || data.slug === 'events' || data.slug === 'warehouses';
  
  const totalBlocks = Array.isArray(data.blocks) ? data.blocks.length : 0;
  const totalPages = Math.ceil(totalBlocks / blocksPerPage);
  const startIndex = currentPage * blocksPerPage;
  const endIndex = startIndex + blocksPerPage;
  const blocksToDisplay = usePagination ? (data.blocks?.slice(startIndex, endIndex) || []) : (data.blocks || []);

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <main className="blog-page">
      <Hero hero={data.hero} />

      <Lead
        eyebrow={data.lead?.eyebrow}
        title={data.lead?.title}
        accent={data.lead?.accent}
        copy={data.lead?.copy}
      />

      {blocksToDisplay.map((b, i) => {
        const blockIndex = usePagination ? startIndex + i : i;
        return <Block key={blockIndex} block={b} flip={b.flip ?? blockIndex % 2 === 1} />;
      })}

      {usePagination && totalPages > 1 && (
        <div className="bp-pagination">
          <button 
            className="bp-pagination__btn" 
            onClick={handlePrevious} 
            disabled={currentPage === 0}
          >
            ← Previous
          </button>
          <span className="bp-pagination__info">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button 
            className="bp-pagination__btn" 
            onClick={handleNext} 
            disabled={currentPage === totalPages - 1}
          >
            Next →
          </button>
        </div>
      )}

      {data.footerCta && (
        <section className="bp-final-cta">
          <h3>
            <Accentify text={data.footerCta.title} accent={data.footerCta.accent} />
          </h3>
          {data.footerCta.copy && <p>{data.footerCta.copy}</p>}
          {data.footerCta.href && data.footerCta.label && (
            <a className="bp-primary" href={data.footerCta.href}>
              {data.footerCta.label}
            </a>
          )}
        </section>
      )}
    </main>
  );
}

/* ---------- prop types ---------- */
Accent.propTypes = { children: PropTypes.node };

Accentify.propTypes = {
  text: PropTypes.string,
  accent: PropTypes.string,
};

Hero.propTypes = {
  hero: PropTypes.shape({
    bgImage: PropTypes.string,
    imagePosition: PropTypes.string,
    bgSize: PropTypes.string,
    kicker: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    dateISO: PropTypes.string,
    readTimeMin: PropTypes.number,
    tags: PropTypes.arrayOf(PropTypes.string),
    author: PropTypes.shape({ name: PropTypes.string, avatar: PropTypes.string }),
  }),
};

Lead.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  accent: PropTypes.string,
  copy: PropTypes.string,
};

Block.propTypes = {
  block: PropTypes.shape({
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    accent: PropTypes.string,
    copy: PropTypes.string,
    image: PropTypes.any,
    imageAlt: PropTypes.string,
    points: PropTypes.arrayOf(PropTypes.string),
    quote: PropTypes.string,
    cite: PropTypes.string,
    buttons: PropTypes.arrayOf(
      PropTypes.shape({ label: PropTypes.string, href: PropTypes.string })
    ),
    flip: PropTypes.bool,
  }),
  flip: PropTypes.bool,
};

WhoWeServePage.propTypes = {
  data: PropTypes.shape({
    hero: Hero.propTypes.hero,
    lead: Lead.propTypes,
    blocks: PropTypes.arrayOf(Block.propTypes.block),
    footerCta: PropTypes.shape({
      title: PropTypes.string,
      accent: PropTypes.string,
      copy: PropTypes.string,
      label: PropTypes.string,
      href: PropTypes.string,
    }),
  }),
};